import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  LinearProgress,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LayoutDashboard, KeyRound, Server, Camera as CameraIconL, FolderKanban } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { lightTheme, darkTheme } from "../../theme/theme";
import { formatToIST, getSystemDetails } from "../../utils/formatting";
import { sortData } from "../../utils/sorting";
import { printReport } from "../../utils/reportPrinter";
import * as licensesApi from "../licenses/licensesApi";
import * as systemsApi from "../systems/systemsApi";
import * as camerasApi from "../cameras/camerasApi";
import * as usersApi from "../users/usersApi";
import * as sessionLogsApi from "../sessionLogs/sessionLogsApi";
import { useSessionTimer } from "./hooks/useSessionTimer";
import "./Dashboard.css";

import Sidebar from "../../shared/components/Sidebar";
import TopBar from "../../shared/components/TopBar";
import { SystemHoverCard } from "../../shared/components/SystemHoverCard";
import { BottomNav } from "../../shared/components/BottomNav";
import DashboardHome from "./DashboardHome";
import LicensesView from "../licenses/LicensesView";
import SystemsView from "../systems/SystemsView";
import CamerasView from "../cameras/CamerasView";
import ProjectsView from "../projects/ProjectsView";
import UsersView from "../users/UsersView";
import ProjectDetailsView from "../projects/ProjectDetailsView";
import LicenseDialog from "../licenses/dialogs/LicenseDialog";
import OfflineKeyDialog from "../licenses/dialogs/OfflineKeyDialog";
import SystemDialog from "../systems/dialogs/SystemDialog";
import CameraDialog from "../cameras/dialogs/CameraDialog";
import UserDialog from "../users/dialogs/UserDialog";
import SessionLogsDialog from "../sessionLogs/dialogs/SessionLogsDialog";
import { ConfirmDeleteDialog } from "../../shared/components/ConfirmDeleteDialog";

function Dashboard() {
  const [currentUser] = useState(localStorage.getItem("username") || "");
  const [licenses, setLicenses] = useState([]);
  const [systems, setSystems] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newLicense, setNewLicense] = useState({ uuid: "", start_date: "", end_date: "", systemOptions: [] });
  const [newSystem, setNewSystem] = useState({ uuid: "", project_id: "", software_id: "", amc: false });
  const [newCamera, setNewCamera] = useState({ uuid: "", camera_serial: "", status: "online", systemOptions: [] });
  const [openDialog, setOpenDialog] = useState({ license: false, system: false, camera: false });
  const [activeView, setActiveView] = useState("dashboard");
  const [loading, setLoading] = useState({ licenses: false, systems: false, cameras: false, projects: false });
  const [, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const onlineCameras = cameras.filter((camera) => camera.status === "online").length;
  const offlineCameras = cameras.filter((camera) => camera.status === "offline").length;
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "" });
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sessionId] = useState(localStorage.getItem("session_id") || "");
  const [sessionLogs, setSessionLogs] = useState([]);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [searchType, setSearchType] = useState("uuid");
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(30);
  const [autoRefreshAnchor, setAutoRefreshAnchor] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(localStorage.getItem("darkMode") === "true" || false);

  // Column visibility states
  const [visibleColumns, setVisibleColumns] = useState({
    licenses: {
      license_id: true,
      uuid: true,
      project_id: true,
      software_id: true,
      start_date: true,
      end_date: true,
      registered_at: true,
      status: true,
      actions: true,
    },
    systems: {
      uuid: true,
      project_id: true,
      software_id: true,
      amc: true,
      registered_at: true,
      actions: true,
    },
    cameras: {
      id: true,
      uuid: true,
      project_id: true,
      camera_serial: true,
      status: true,
      registered_at: true,
      actions: true,
    },
  });

  // Column visibility menu state
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [activeColumnTable, setActiveColumnTable] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [offlineKeyDialog, setOfflineKeyDialog] = useState({
    open: false,
    key: "",
    license_id: "",
    warning: null,
    loading: false,
  });
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchAnchor, setGlobalSearchAnchor] = useState(null);
  const globalSearchRef = React.useRef(null);
  const [systemSearch, setSystemSearch] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [displayedLicenses, setDisplayedLicenses] = useState(0);
  const [displayedSystems, setDisplayedSystems] = useState(0);
  const [displayedCameras, setDisplayedCameras] = useState(0);
  const [displayedProjects, setDisplayedProjects] = useState(0);

  // Split screen state
  const [splitScreen, setSplitScreen] = useState(false);

  // System hover card state
  const [hoverCard, setHoverCard] = useState({ anchor: null, system: null });

  // Toggle column visibility
  const toggleColumn = (tableType, column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [column]: !prev[tableType][column]
      }
    }));
  };

  // Open column visibility menu
  const handleColumnMenuOpen = (event, tableType) => {
    setColumnMenuAnchor(event.currentTarget);
    setActiveColumnTable(tableType);
  };

  // Close column visibility menu
  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
    setActiveColumnTable(null);
  };

  // Use dynamic theme based on darkMode state
  const theme = darkMode ? darkTheme : lightTheme;

  // Copy helper
  const handleCopy = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // System hover card handlers
  const handleSystemHover = (event, uuid) => {
    const system = systems.find(s => s.uuid === uuid);
    if (system) setHoverCard({ anchor: event.currentTarget, system });
  };
  const handleSystemHoverClose = () => setHoverCard({ anchor: null, system: null });

  // Sorting states
  const [sortConfig, setSortConfig] = useState({
    licenses: { key: 'end_date', direction: 'asc' },
    systems: { key: 'registered_at', direction: 'asc' },
    cameras: { key: 'registered_at', direction: 'asc' },
  });

  // Sidebar navigation items
  const sidebarItems = [
    { text: "Dashboard", icon: <LayoutDashboard size={20} />, view: "dashboard", badge: null },
    { text: "Licenses", icon: <KeyRound size={20} />, view: "licenses", badge: licenses.length },
    { text: "Systems", icon: <Server size={20} />, view: "systems", badge: systems.length },
    { text: "Cameras", icon: <CameraIconL size={20} />, view: "cameras", badge: cameras.length },
    { text: "Projects", icon: <FolderKanban size={20} />, view: "projects", badge: projects.length },
  ];

  // Handle sort request
  const handleSort = (tableType, key) => {
    setSortConfig(prev => {
      const current = prev[tableType];
      const newDirection = current.key === key && current.direction === 'asc' ? 'desc' : 'asc';

      return {
        ...prev,
        [tableType]: { key, direction: newDirection }
      };
    });
  };

  // Fetch data on component mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (currentUser === "admin") {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchAllData();
    }, autoRefreshInterval * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, autoRefreshInterval]);

  useSessionTimer(logout);

  useEffect(() => {
    const animateValue = (setter, target) => {
      const duration = 800;
      const start = Date.now();
      const tick = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setter(Math.round(eased * target));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    animateValue(setDisplayedLicenses, licenses.length);
    animateValue(setDisplayedSystems, systems.length);
    animateValue(setDisplayedCameras, cameras.length);
    animateValue(setDisplayedProjects, projects.length);
  }, [licenses.length, systems.length, cameras.length, projects.length]);

  const fetchAllData = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchLicenses(),
      fetchSystems(),
      fetchCameras(),
      fetchProjects()
    ]);
    setTimeout(() => setRefreshing(false), 500);
  };

  const fetchSystemOptions = async () => {
    try {
      const response = await systemsApi.getSystems();
      return response.data.map(system => ({
        value: system.uuid,
        label: `${system.uuid}`,
        project_id: system.project_id || 'No Project',
        software_id: system.software_id || 'No Software',
        displayText: `${system.uuid.substring(0, 8)}... - ${system.project_id || 'No Project'}`
      }));
    } catch (err) {
      console.error("Error fetching systems:", err);
      return [];
    }
  };

  // Fetch licenses
  const fetchLicenses = () => {
    setLoading((prev) => ({ ...prev, licenses: true }));
    return licensesApi.getLicenses()
      .then((res) => {
        const enhancedLicenses = res.data.map(license => ({
          ...license,
          systemDetails: getSystemDetails(systems, license.uuid)
        }));
        setLicenses(enhancedLicenses);
      })
      .catch((err) => setError("Error fetching licenses: " + err.message))
      .finally(() => setLoading((prev) => ({ ...prev, licenses: false })));
  };

  // Fetch systems
  const fetchSystems = () => {
    setLoading((prev) => ({ ...prev, systems: true }));
    return systemsApi.getSystems()
      .then((res) => setSystems(res.data))
      .catch((err) => setError("Error fetching systems: " + err.message))
      .finally(() => setLoading((prev) => ({ ...prev, systems: false })));
  };

  // Fetch cameras
  const fetchCameras = () => {
    setLoading((prev) => ({ ...prev, cameras: true }));
    return camerasApi.getCameras()
      .then((res) => {
        setCameras(res.data);
      })
      .catch((err) => {
        setError("Error fetching cameras: " + err.message);
      })
      .finally(() => setLoading((prev) => ({ ...prev, cameras: false })));
  };

  // Fetch projects with complete data
  const fetchProjects = async () => {
    setLoading((prev) => ({ ...prev, projects: true }));
    try {
      const systemsResponse = await systemsApi.getSystems();
      const camerasResponse = await camerasApi.getCameras();
      const licensesResponse = await licensesApi.getLicenses();

      const allSystems = systemsResponse.data;
      const allCameras = camerasResponse.data;
      const allLicenses = licensesResponse.data;

      const systemsWithProjectId = allSystems.filter((system) => system.project_id);

      const projectsMap = systemsWithProjectId.reduce((acc, system) => {
        if (!acc[system.project_id]) {
          const projectCameras = allCameras.filter(camera =>
            allSystems.some(s => s.uuid === camera.uuid && s.project_id === system.project_id)
          );

          const projectLicenses = allLicenses.filter(license =>
            allSystems.some(s => s.uuid === license.uuid && s.project_id === system.project_id)
          );

          acc[system.project_id] = {
            project_id: system.project_id,
            systems: [],
            cameras: projectCameras,
            licenses: projectLicenses,
            total_cameras: projectCameras.length,
            online_cameras: projectCameras.filter(c => c.status === "online").length,
            offline_cameras: projectCameras.filter(c => c.status === "offline").length,
            total_licenses: projectLicenses.length,
            active_licenses: projectLicenses.filter(l => new Date(l.end_date) >= new Date()).length,
            expired_licenses: projectLicenses.filter(l => new Date(l.end_date) < new Date()).length,
          };
        }

        acc[system.project_id].systems.push(system);
        return acc;
      }, {});

      const projects = Object.values(projectsMap).sort((a, b) =>
        a.project_id.localeCompare(b.project_id)
      );

      setProjects(projects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Error fetching projects: " + err.message);
    } finally {
      setLoading((prev) => ({ ...prev, projects: false }));
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await usersApi.getUsers();
      setUsers(response.data);
    } catch (err) {
      setSnackbarMessage("Failed to fetch users: " + err.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSessionLogs = async () => {
    try {
      const response = await sessionLogsApi.getSessionLogs();
      setSessionLogs(response.data);
      setShowLogsDialog(true);
    } catch (err) {
      console.error("Error fetching logs:", err);
      setSnackbarMessage(
        err.response?.data?.detail ||
        "Failed to fetch session logs. Please ensure you're logged in as admin."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId);
    setActiveView("projectDetails");
    localStorage.setItem("previousView", "projects");
  };

  const [isEditMode, setIsEditMode] = useState(false);

  const handlePageChange = (event, value) => setPage(value);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleOpenDialog = async (type) => {
    if (type === "license" || type === "camera") {
      const systemOptions = await fetchSystemOptions();
      if (type === "license") {
        setNewLicense(prev => ({ ...prev, systemOptions }));
      } else {
        setNewCamera(prev => ({ ...prev, systemOptions }));
      }
    }
    setOpenDialog({ ...openDialog, [type]: true });
  };

  const handleCloseDialog = (type) => {
    setOpenDialog({ ...openDialog, [type]: false });
    if (type === "license") {
      setSystemSearch('');
      setNewLicense({
        uuid: "",
        start_date: "",
        end_date: "",
        systemOptions: newLicense.systemOptions
      });
    } else if (type === "system") {
      setIsEditMode(false);
      setNewSystem({ uuid: "", project_id: "", software_id: "", amc: false });
    } else if (type === "camera") {
      setNewCamera({
        uuid: "",
        camera_serial: "",
        status: "online",
        systemOptions: newCamera.systemOptions,
        id: null
      });
    }
  };

  const addLicense = async () => {
    const isValidUUID = systems.some((system) => system.uuid === newLicense.uuid);

    if (!isValidUUID) {
      setSnackbarMessage("Invalid System UID. Please register the system first.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      await licensesApi.createLicense({ ...newLicense, session_id: sessionId });

      setSnackbarMessage("License added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchAllData();
      handleCloseDialog("license");
    } catch (err) {
      setSnackbarMessage("Failed to add license: " + err.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const addSystem = async () => {
    try {
      await systemsApi.createSystem({ ...newSystem, session_id: sessionId });

      setSnackbarMessage("System added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchAllData();
      handleCloseDialog("system");
      setNewSystem({ uuid: "", project_id: "", software_id: "", amc: false });
    } catch (err) {
      setSnackbarMessage("Failed to add system: " + err.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const editSystem = async () => {
    const updatedFields = {};
    if (newSystem.project_id) updatedFields.project_id = newSystem.project_id;
    if (newSystem.software_id) updatedFields.software_id = newSystem.software_id;
    if (newSystem.amc !== undefined) updatedFields.amc = newSystem.amc;

    try {
      await systemsApi.updateSystem(newSystem.uuid, updatedFields);

      setSnackbarMessage("System updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchAllData();
      handleCloseDialog("system");
      setNewSystem({ uuid: "", project_id: "", software_id: "", amc: false });
    } catch (err) {
      setSnackbarMessage("Failed to update system: " + err.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const addCamera = async () => {
    const isEdit = !!newCamera.id;

    try {
      const apiCall = isEdit
        ? camerasApi.updateCamera(newCamera.id, { ...newCamera, session_id: sessionId })
        : camerasApi.createCamera({ ...newCamera, session_id: sessionId });

      await apiCall;

      setSnackbarMessage(`Camera ${isEdit ? "updated" : "added"} successfully!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchAllData();
      handleCloseDialog("camera");
    } catch (err) {
      setSnackbarMessage(`Failed to ${isEdit ? "update" : "add"} camera: ${err.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const downloadLicenseFile = async (license_id) => {
    try {
      const response = await licensesApi.downloadLicenseFile(license_id);

      const disposition = response.headers["content-disposition"];
      let filename = `${license_id}.lic`;
      if (disposition) {
        const match = disposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 403) {
        setSnackbarMessage("Only admin can download license files");
      } else {
        setSnackbarMessage("Failed to download license file: " + err.message);
      }
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const generateOfflineKey = async (license_id) => {
    setOfflineKeyDialog({ open: true, key: "", license_id, warning: null, loading: true });
    try {
      const response = await licensesApi.getOfflineKey(license_id);
      setOfflineKeyDialog({
        open: true,
        key: response.data.offline_key,
        license_id,
        warning: response.data.warning,
        loading: false,
      });
    } catch (err) {
      setSnackbarMessage(
        "Failed to generate offline key: " + (err.response?.data?.detail || err.message)
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setOfflineKeyDialog({ open: false, key: "", license_id: "", warning: null, loading: false });
    }
  };

  const deleteLicense = (license_id) => {
    if (currentUser !== "admin") {
      setSnackbarMessage("Only admin can delete licenses");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setDeleteDialog({
      open: true,
      title: 'Delete License',
      message: 'Are you sure you want to delete this license? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await licensesApi.deleteLicense(license_id, sessionId);
          setSnackbarMessage("License deleted successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          fetchAllData();
        } catch (err) {
          setSnackbarMessage("Failed to delete license: " + err.message);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }
    });
  };

  const deleteSystem = (uuid) => {
    if (currentUser !== "admin") {
      setSnackbarMessage("Only admin can delete systems");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setDeleteDialog({
      open: true,
      title: 'Delete System',
      message: 'Are you sure you want to delete this system? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await systemsApi.deleteSystem(uuid, sessionId);
          setSnackbarMessage("System deleted successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          fetchAllData();
        } catch (err) {
          setSnackbarMessage("Failed to delete system: " + err.message);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }
    });
  };

  const deleteCamera = (id) => {
    if (currentUser !== "admin") {
      setSnackbarMessage("Only admin can delete cameras");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setDeleteDialog({
      open: true,
      title: 'Delete Camera',
      message: 'Are you sure you want to delete this camera? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await camerasApi.deleteCamera(id, sessionId);
          setSnackbarMessage("Camera deleted successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          fetchAllData();
        } catch (err) {
          setSnackbarMessage("Failed to delete camera: " + err.message);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }
    });
  };

  const handleAddSystem = () => {
    setNewSystem({ uuid: "", project_id: "", software_id: "", amc: false });
    setIsEditMode(false);
    handleOpenDialog("system");
  };

  const handleEditSystem = (system) => {
    setNewSystem(system);
    setIsEditMode(true);
    handleOpenDialog("system");
  };

  const handleEditCamera = async (camera) => {
    try {
      const systemOptions = await fetchSystemOptions();
      setNewCamera({
        ...camera,
        systemOptions,
        loading: false
      });
      handleOpenDialog("camera");
    } catch (err) {
      setSnackbarMessage("Failed to load camera data");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Filter data with enhanced search
  const filteredLicenses = licenses.filter((license) => {
    const systemDetails = getSystemDetails(systems, license.uuid);
    const searchLower = searchTerm.toLowerCase();

    switch (searchType) {
      case "project_id":
        return systemDetails?.project_id?.toLowerCase().includes(searchLower) || false;
      case "software_id":
        return systemDetails?.software_id?.toLowerCase().includes(searchLower) || false;
      default:
        return String(license[searchType] || '').toLowerCase().includes(searchLower);
    }
  });

  const filteredSystems = systems.filter((system) => {
    const searchLower = searchTerm.toLowerCase();
    switch (searchType) {
      case "project_id":
        return system.project_id?.toLowerCase().includes(searchLower) || false;
      case "software_id":
        return system.software_id?.toLowerCase().includes(searchLower) || false;
      default:
        return String(system[searchType] || '').toLowerCase().includes(searchLower);
    }
  });

  const filteredCameras = cameras.filter((camera) => {
    const systemDetails = getSystemDetails(systems, camera.uuid);
    const searchLower = searchTerm.toLowerCase();

    switch (searchType) {
      case "project_id":
        return systemDetails?.project_id?.toLowerCase().includes(searchLower) || false;
      default:
        return String(camera[searchType] || '').toLowerCase().includes(searchLower);
    }
  });

  // Apply sorting to filtered data
  const sortedLicenses = sortData(filteredLicenses, sortConfig.licenses, systems);
  const sortedSystems = sortData(filteredSystems, sortConfig.systems, systems);
  const sortedCameras = sortData(filteredCameras, sortConfig.cameras, systems);

  // Paginate the sorted data
  const paginatedLicenses = sortedLicenses.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const paginatedSystems = sortedSystems.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const paginatedCameras = sortedCameras.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Print handler
  const handlePrint = (section) => {
    printReport(section, sortedLicenses, sortedSystems, sortedCameras, systems);
  };

  const addUser = async () => {
    try {
      await usersApi.createUser({ ...newUser, session_id: sessionId });

      setSnackbarMessage("User added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpenUserDialog(false);
      setNewUser({ username: "", password: "" });
    } catch (err) {
      setSnackbarMessage("Failed to add user: " + err.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const deleteUser = (userId) => {
    const userToDelete = users.find(user => user.id === userId);
    if (userToDelete && userToDelete.username === "admin") {
      setSnackbarMessage("Cannot delete admin user");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setDeleteDialog({
      open: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await usersApi.deleteUser(userId, sessionId);
          setSnackbarMessage("User deleted successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          fetchUsers();
        } catch (err) {
          setSnackbarMessage("Failed to delete user: " + err.message);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }
    });
  };

  const activeLicenses = licenses.filter((license) => new Date(license.end_date) >= new Date()).length;
  const expiredLicenses = licenses.filter((license) => new Date(license.end_date) < new Date()).length;
  const systemsWithAMC = systems.filter((system) => system.amc).length;
  const systemsWithoutAMC = systems.filter((system) => !system.amc).length;
  const expiringCount = licenses.filter(license => {
    const days = Math.floor((new Date(license.end_date) - new Date()) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 30;
  }).length;
  const notifCount = expiringCount + offlineCameras;
  const totalCameras = cameras.length;
  const systemUtilization = systems.length > 0 ? ((systemsWithAMC / systems.length) * 100).toFixed(1) : 0;
  const cameraOnlineRatio = totalCameras > 0 ? ((onlineCameras / totalCameras) * 100).toFixed(1) : 0;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
        <Sidebar
          isMobile={isMobile}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          darkMode={darkMode}
          theme={theme}
          currentUser={currentUser}
          activeView={activeView}
          setActiveView={setActiveView}
          sidebarItems={sidebarItems}
          expiringCount={expiringCount}
          refreshing={refreshing}
          fetchAllData={fetchAllData}
          logout={logout}
          formatToIST={formatToIST}
        />

        <Box sx={{
          flex: 1,
          width: '100%',
          background: darkMode
            ? 'linear-gradient(-45deg, #060B14, #0E1626, #060B14, #0A1220)'
            : 'linear-gradient(-45deg, #F4F7FA, #E3F4F6, #F4F7FA, #EAF0F4)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 15s ease infinite',
        }}>
          <TopBar
            isMobile={isMobile}
            setSidebarOpen={setSidebarOpen}
            activeView={activeView}
            sidebarItems={sidebarItems}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            theme={theme}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            globalSearchAnchor={globalSearchAnchor}
            setGlobalSearchAnchor={setGlobalSearchAnchor}
            globalSearchRef={globalSearchRef}
            licenses={licenses}
            systems={systems}
            cameras={cameras}
            setActiveView={setActiveView}
            setSearchTerm={setSearchTerm}
            splitScreen={splitScreen}
            setSplitScreen={setSplitScreen}
            refreshing={refreshing}
            fetchAllData={fetchAllData}
            autoRefresh={autoRefresh}
            setAutoRefresh={setAutoRefresh}
            autoRefreshInterval={autoRefreshInterval}
            setAutoRefreshInterval={setAutoRefreshInterval}
            autoRefreshAnchor={autoRefreshAnchor}
            setAutoRefreshAnchor={setAutoRefreshAnchor}
            notifAnchor={notifAnchor}
            setNotifAnchor={setNotifAnchor}
            notifCount={notifCount}
            expiringCount={expiringCount}
            offlineCameras={offlineCameras}
            currentUser={currentUser}
            fetchSessionLogs={fetchSessionLogs}
          />

          <Container maxWidth={false} sx={{ mt: 4, mb: isMobile ? 9 : 4, px: isMobile ? 2 : 4 }}>
            {refreshing && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
              </Box>
            )}

            {activeView === "dashboard" && (
              <DashboardHome
                darkMode={darkMode}
                theme={theme}
                currentUser={currentUser}
                licenses={licenses}
                systems={systems}
                cameras={cameras}
                projects={projects}
                displayedLicenses={displayedLicenses}
                displayedSystems={displayedSystems}
                displayedCameras={displayedCameras}
                displayedProjects={displayedProjects}
                activeLicenses={activeLicenses}
                expiredLicenses={expiredLicenses}
                systemsWithAMC={systemsWithAMC}
                systemsWithoutAMC={systemsWithoutAMC}
                systemUtilization={systemUtilization}
                cameraOnlineRatio={cameraOnlineRatio}
                onlineCameras={onlineCameras}
                offlineCameras={offlineCameras}
                totalCameras={totalCameras}
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                handleOpenDialog={handleOpenDialog}
                handleAddSystem={handleAddSystem}
                handleProjectSelect={handleProjectSelect}
                setActiveView={setActiveView}
                isMobile={isMobile}
              />
            )}

            {activeView === "licenses" && (
              <LicensesView
                darkMode={darkMode}
                theme={theme}
                licenses={licenses}
                systems={systems}
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                searchType={searchType}
                setSearchType={setSearchType}
                sortConfig={sortConfig}
                handleSort={handleSort}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                toggleColumn={toggleColumn}
                columnMenuAnchor={columnMenuAnchor}
                activeColumnTable={activeColumnTable}
                handleColumnMenuOpen={handleColumnMenuOpen}
                handleColumnMenuClose={handleColumnMenuClose}
                page={page}
                handlePageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                isMobile={isMobile}
                loading={loading}
                filteredLicenses={filteredLicenses}
                paginatedLicenses={paginatedLicenses}
                copiedField={copiedField}
                handleCopy={handleCopy}
                handleSystemHover={handleSystemHover}
                handleSystemHoverClose={handleSystemHoverClose}
                handleOpenDialog={handleOpenDialog}
                handlePrint={handlePrint}
                downloadLicenseFile={downloadLicenseFile}
                generateOfflineKey={generateOfflineKey}
                deleteLicense={deleteLicense}
                splitScreen={splitScreen}
                currentUser={currentUser}
              />
            )}

            {activeView === "systems" && (
              <SystemsView
                darkMode={darkMode}
                theme={theme}
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                searchType={searchType}
                setSearchType={setSearchType}
                sortConfig={sortConfig}
                handleSort={handleSort}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                toggleColumn={toggleColumn}
                columnMenuAnchor={columnMenuAnchor}
                activeColumnTable={activeColumnTable}
                handleColumnMenuOpen={handleColumnMenuOpen}
                handleColumnMenuClose={handleColumnMenuClose}
                page={page}
                handlePageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                isMobile={isMobile}
                loading={loading}
                filteredSystems={filteredSystems}
                paginatedSystems={paginatedSystems}
                copiedField={copiedField}
                handleCopy={handleCopy}
                handleSystemHover={handleSystemHover}
                handleSystemHoverClose={handleSystemHoverClose}
                handleAddSystem={handleAddSystem}
                handleEditSystem={handleEditSystem}
                handlePrint={handlePrint}
                deleteSystem={deleteSystem}
                fetchSystemOptions={fetchSystemOptions}
                setNewLicense={setNewLicense}
                setSystemSearch={setSystemSearch}
                setOpenDialog={setOpenDialog}
              />
            )}

            {activeView === "cameras" && (
              <CamerasView
                darkMode={darkMode}
                theme={theme}
                systems={systems}
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                searchType={searchType}
                setSearchType={setSearchType}
                sortConfig={sortConfig}
                handleSort={handleSort}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                toggleColumn={toggleColumn}
                columnMenuAnchor={columnMenuAnchor}
                activeColumnTable={activeColumnTable}
                handleColumnMenuOpen={handleColumnMenuOpen}
                handleColumnMenuClose={handleColumnMenuClose}
                page={page}
                handlePageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                isMobile={isMobile}
                loading={loading}
                filteredCameras={filteredCameras}
                paginatedCameras={paginatedCameras}
                copiedField={copiedField}
                handleCopy={handleCopy}
                handleOpenDialog={handleOpenDialog}
                handleEditCamera={handleEditCamera}
                handlePrint={handlePrint}
                deleteCamera={deleteCamera}
              />
            )}

            {activeView === "projects" && (
              <ProjectsView
                darkMode={darkMode}
                theme={theme}
                projects={projects}
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                loading={loading}
                isMobile={isMobile}
                setSelectedProject={setSelectedProject}
                setActiveView={setActiveView}
              />
            )}

            {activeView === "users" && currentUser === "admin" && (
              <UsersView
                darkMode={darkMode}
                theme={theme}
                users={users}
                loadingUsers={loadingUsers}
                isMobile={isMobile}
                fetchSessionLogs={fetchSessionLogs}
                setOpenUserDialog={setOpenUserDialog}
                deleteUser={deleteUser}
              />
            )}

            {activeView === "projectDetails" && selectedProject && (
              <ProjectDetailsView
                darkMode={darkMode}
                theme={theme}
                selectedProject={selectedProject}
                setActiveView={setActiveView}
                setSelectedProject={setSelectedProject}
                setSearchTerm={setSearchTerm}
                loading={loading}
                systems={systems}
                cameras={cameras}
                licenses={licenses}
                visibleColumns={visibleColumns}
                sortConfig={sortConfig}
                handleSort={handleSort}
                currentUser={currentUser}
                isMobile={isMobile}
                handleEditSystem={handleEditSystem}
                deleteSystem={deleteSystem}
                handleEditCamera={handleEditCamera}
                deleteCamera={deleteCamera}
                downloadLicenseFile={downloadLicenseFile}
                generateOfflineKey={generateOfflineKey}
                deleteLicense={deleteLicense}
                fetchSystemOptions={fetchSystemOptions}
                setNewLicense={setNewLicense}
                setSystemSearch={setSystemSearch}
                setOpenDialog={setOpenDialog}
              />
            )}
          </Container>
        </Box>

        {isMobile && (
          <BottomNav
            activeView={activeView}
            setActiveView={setActiveView}
            sidebarItems={sidebarItems}
            darkMode={darkMode}
            theme={theme}
          />
        )}

        <SystemHoverCard
          hoverCard={hoverCard}
          handleSystemHoverClose={handleSystemHoverClose}
          licenses={licenses}
          theme={theme}
          darkMode={darkMode}
          formatToIST={formatToIST}
        />

        <LicenseDialog
          open={openDialog.license}
          onClose={() => handleCloseDialog("license")}
          theme={theme}
          darkMode={darkMode}
          systemSearch={systemSearch}
          setSystemSearch={setSystemSearch}
          newLicense={newLicense}
          setNewLicense={setNewLicense}
          addLicense={addLicense}
        />

        <SystemDialog
          open={openDialog.system}
          onClose={() => handleCloseDialog("system")}
          theme={theme}
          darkMode={darkMode}
          isEditMode={isEditMode}
          newSystem={newSystem}
          setNewSystem={setNewSystem}
          addSystem={addSystem}
          editSystem={editSystem}
        />

        <CameraDialog
          open={openDialog.camera}
          onClose={() => handleCloseDialog("camera")}
          darkMode={darkMode}
          newCamera={newCamera}
          setNewCamera={setNewCamera}
          addCamera={addCamera}
        />

        <OfflineKeyDialog
          offlineKeyDialog={offlineKeyDialog}
          setOfflineKeyDialog={setOfflineKeyDialog}
          theme={theme}
          darkMode={darkMode}
          copiedField={copiedField}
          handleCopy={handleCopy}
        />

        <UserDialog
          open={openUserDialog}
          onClose={() => setOpenUserDialog(false)}
          theme={theme}
          darkMode={darkMode}
          newUser={newUser}
          setNewUser={setNewUser}
          addUser={addUser}
        />

        <SessionLogsDialog
          open={showLogsDialog}
          onClose={() => setShowLogsDialog(false)}
          theme={theme}
          darkMode={darkMode}
          sessionLogs={sessionLogs}
          formatToIST={formatToIST}
        />

        <ConfirmDeleteDialog
          deleteDialog={deleteDialog}
          setDeleteDialog={setDeleteDialog}
          theme={theme}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{
              width: '100%',
              borderRadius: '50px',
              px: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin { animation: spin 1s linear infinite; }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          * {
            scrollbar-width: thin;
            scrollbar-color: ${darkMode ? '#2FE4D6' : theme.palette.primary.main} ${darkMode ? '#0E1626' : theme.palette.background.default};
          }
          *::-webkit-scrollbar { width: 6px; height: 6px; }
          *::-webkit-scrollbar-track {
            background: ${darkMode ? '#0E1626' : theme.palette.background.default};
          }
          *::-webkit-scrollbar-thumb {
            background-color: ${darkMode ? '#2FE4D6' : theme.palette.primary.main};
            border-radius: 20px;
            opacity: 0.5;
          }
          *::-webkit-scrollbar-thumb:hover {
            background-color: ${darkMode ? '#0C8599' : theme.palette.primary.dark};
          }
        `}</style>
      </Box>
    </ThemeProvider>
  );
}

export default Dashboard;
